import React, { useState } from "react";
import { HiOutlineArrowsRightLeft } from "react-icons/hi2";
import { IoChevronDown } from "react-icons/io5";
import { formatDateForDisplay } from "../../utils/searchUtils";
import { formatPassengerDisplay } from "../../utils/passengerUtils";

interface SearchSummaryProps {
    searchData: {
        tripType: "RT" | "OW" | "MC";
        origin: string;
        destination: string;
        departureDate: string;
        returnDate?: string;
        adults: number;
        children: number;
        infants: number;
    };
    onNewSearch?: () => void;
}

const SearchSummary: React.FC<SearchSummaryProps> = ({ searchData }) => {
    const [showReturnFromAnother, setShowReturnFromAnother] = useState(false);

    return (
        <div className="bg-white">
            <div className="max-w-[1120px] mx-auto px-7 py-2">
                {/* Close Button Row */}
                <div className="flex justify-end -mr-14">
                    <button className="text-3xl text-gray-700 leading-none p-2">
                        ×
                    </button>
                </div>

                {/* Trip Type Tabs and Required Info Row */}
                <div className="flex items-center justify-between mb-6 ml-2">
                    <div className="flex items-center gap-8 text-[#383838]">
                        <button
                            className={`px-3 pb-4 transition-all relative ${
                                searchData.tripType === "OW"
                                    ? "border-b-2 border-sas-blue"
                                    : "border-b-2 border-gray-100"
                            }`}
                        >
                            One way
                        </button>
                        <button
                            className={`px-3 pb-4 font-medium transition-all relative ${
                                searchData.tripType === "RT"
                                    ? "border-b-2 border-sas-blue"
                                    : ""
                            }`}
                        >
                            Round trip
                        </button>
                        <button
                            onClick={() =>
                                setShowReturnFromAnother(!showReturnFromAnother)
                            }
                            className="px-3 pb-4 border-b-2 border-gray-100"
                        >
                            Return from another airport
                        </button>
                    </div>
                    <div className="text-xs text-gray-600 -mr-12">
                        *Required information
                    </div>
                </div>

                {/* Search Fields */}
                <div className="flex items-center gap-2 mb-4 -mt-3 ml-2">
                    {/* From Field */}
                    <div
                        className="relative"
                        style={{ minWidth: "197px", maxWidth: "197px" }}
                    >
                        <label className="absolute top-2 left-4 text-xs text-[#383838]">
                            From*
                        </label>
                        <input
                            type="text"
                            value={`${searchData.origin} OSL`}
                            readOnly
                            className="w-full h-[60px] pt-5 pb-2 px-4 border border-gray-500 rounded-lg bg-white text-base text-[#383838]"
                        />
                    </div>

                    {/* Swap Button */}
                    <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        {React.createElement(HiOutlineArrowsRightLeft as any, {
                            className: "text-xl text-gray-700",
                        })}
                    </button>

                    {/* To Field */}
                    <div
                        className="relative"
                        style={{ minWidth: "197px", maxWidth: "197px" }}
                    >
                        <label className="absolute top-2 left-4 text-xs text-[#383838]">
                            To*
                        </label>
                        <input
                            type="text"
                            value={`New York, NY ${searchData.destination}`}
                            readOnly
                            className="w-full h-[60px] pt-5 pb-2 px-4 border border-gray-500 rounded-lg bg-white text-base text-[#383838]"
                        />
                    </div>

                    {/* Departure Date */}
                    <div
                        className="relative"
                        style={{
                            minWidth:
                                searchData.tripType === "RT"
                                    ? "239px"
                                    : "300px",
                            maxWidth:
                                searchData.tripType === "RT"
                                    ? "239px"
                                    : "400px",
                            flex:
                                searchData.tripType === "OW"
                                    ? "1 1 auto"
                                    : "0 0 auto",
                        }}
                    >
                        <label className="absolute top-2 left-4 text-xs text-[#383838]">
                            Departure date*
                        </label>
                        <input
                            type="text"
                            value={formatDateForDisplay(
                                searchData.departureDate
                            )}
                            readOnly
                            className="w-full h-[60px] pt-5 pb-2 px-4 border border-gray-500 rounded-lg bg-white text-base text-[#383838]"
                        />
                    </div>

                    {/* Return Date */}
                    {searchData.tripType === "RT" && (
                        <div
                            className="relative"
                            style={{ minWidth: "239px", maxWidth: "239px" }}
                        >
                            <label className="absolute top-2 left-4 text-xs text-[#383838]">
                                Return date*
                            </label>
                            <input
                                type="text"
                                value={formatDateForDisplay(
                                    searchData.returnDate ||
                                        searchData.departureDate
                                )}
                                readOnly
                                className="w-full h-[60px] pt-5 pb-2 px-4 border border-gray-500 rounded-lg bg-white text-base text-[#383838]"
                            />
                        </div>
                    )}

                    {/* Travelers */}
                    <div
                        className="relative"
                        style={{ minWidth: "160px", maxWidth: "160px" }}
                    >
                        <label className="absolute top-2 left-4 text-xs text-[#383838]">
                            Travelers*
                        </label>
                        <input
                            type="text"
                            value={formatPassengerDisplay(
                                searchData.adults,
                                searchData.children,
                                searchData.infants
                            )}
                            readOnly
                            className="w-full h-[60px] pt-5 pb-2 px-4 pr-10 border border-gray-500 rounded-lg bg-white text-base text-[#383838] cursor-pointer"
                        />
                        {React.createElement(IoChevronDown as any, {
                            className:
                                "absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 text-lg pointer-events-none",
                        })}
                    </div>
                </div>

                {/* Pay with Options and Search Button Row */}
                <div
                    className="flex items-start justify-between pb-6 px-2"
                    style={{ width: "1152px" }}
                >
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-600">Pay with:</span>
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                name="payment"
                                value="EUR"
                                defaultChecked
                                className="text-sas-blue"
                            />
                            <span className="text-sm text-[#424242]">EUR</span>
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                name="payment"
                                value="Points"
                                className="text-sas-blue"
                            />
                            <span className="text-sm text-[#424242]">
                                Points
                            </span>
                        </label>
                    </div>

                    {/* Search Button */}
                    <button className="h-12 px-7 bg-[#0205bd] text-white rounded-full font-semibold text-sm uppercase tracking-wide mr-6">
                        SEARCH
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SearchSummary;
